<script setup lang="ts">
import AdjustSpeedIcon from '@/assets/svgs/adjust_speed.svg'
import FullScreenIcon from '@/assets/svgs/full_screen.svg'
import FullScreenOffIcon from '@/assets/svgs/full_screen_off.svg'

import CloseIcon from '@/assets/svgs/close.svg'
import MenuIcon from '@/assets/svgs/hamburger.svg'
import OffCameraIcon from '@/assets/svgs/off_camera.svg'
import OpenCameraIcon from '@/assets/svgs/open_camera.svg'
import PauseIcon from '@/assets/svgs/pause.svg'
import PlayIcon from '@/assets/svgs/play.svg'
import PlayRedIcon from '@/assets/svgs/play_red.svg'
import RepeatRedIcon from '@/assets/svgs/repeat_red.svg'
import RepeatWhiteIcon from '@/assets/svgs/repeat_white.svg'
import BaseFilter from '@/components/base/BaseFilter.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { FilterOption } from '@/types/filter.types'
import type { LectureDetail } from '@/types/lecture.types'
import Player from '@vimeo/player'
import { computed, onMounted, onUnmounted, ref } from 'vue'

const props = defineProps<{
  lecture: LectureDetail
}>()

const isMobile = useIsMobile()

const vimeoUrl = computed(() => {
  if (props.lecture.vimeoUrl.includes('?')) {
    return props.lecture.vimeoUrl + '&controls=false&autoplay=true'
  }
  return props.lecture.vimeoUrl + '?controls=false&autoplay=true'
})

const onWebCam = ref(false)
const isFullscreen = ref(false)
const showChapters = ref(false)
const playerContainer = ref<HTMLElement | null>(null)
const chapters = ref<{ title: string; startTime: number; index?: number }[]>([])
const videoElement = ref<HTMLVideoElement | null>(null)
const mediaStream = ref<MediaStream | null>(null)
const currentSpeed = ref(1.0)
const filterOptions = ref<FilterOption[]>([
  { idx: 0, name: '0.5x', data: 0.5 },
  { idx: 1, name: '0.75x', data: 0.75 },
  { idx: 2, name: '1x (표준)', data: 1.0 },
  { idx: 3, name: '1.25x', data: 1.25 },
  { idx: 4, name: '1.5x', data: 1.5 },
  { idx: 5, name: '1.75x', data: 1.75 },
  { idx: 6, name: '2x', data: 2.0 },
])
const selectedFilter = ref<FilterOption>(filterOptions.value[2])
const currentTime = ref(0)
const duration = ref(0)
const isPaused = ref(true)
const repeatStartTime = ref<number | null>(null)
const repeatEndTime = ref<number | null>(null)
const repeatMode = ref<'none' | 'start' | 'end'>('none')
const showControls = ref(true)
const showCursor = ref(true)
const controlsTimeout = ref<number | null>(null)
const cursorTimeout = ref<number | null>(null)
const isIOS = ref(false)

const shouldShowControls = computed(() => {
  return isPaused.value || showControls.value
})

const shouldShowCursor = computed(() => {
  // 모바일 환경에서는 항상 true 반환
  if (isMobile.value) {
    return true
  }
  return isPaused.value || showCursor.value
})

onMounted(() => {
  // iOS 기기 확인
  const userAgent = navigator.userAgent.toLowerCase()
  const isIPad = /ipad/.test(userAgent)
  const isIPhone = /iphone/.test(userAgent)
  const isIPod = /ipod/.test(userAgent)
  const isWebKit = /webkit/.test(userAgent)
  const isIOSDevice = (isIPad || isIPhone || isIPod) && isWebKit
  isIOS.value = isIOSDevice

  const iframe = document.querySelector('#vimeo-player')
  if (!iframe) return

  const player = new Player(iframe as HTMLElement)

  player.on('play', () => {
    isPaused.value = false
    // 재생 시작 시 3초 후 컨트롤과 커서 숨기기
    setTimeout(() => {
      if (!isPaused.value) {
        showControls.value = false
        showCursor.value = false
      }
    }, 3000)
  })

  player.on('pause', () => {
    isPaused.value = true
    // 일시정지 시 컨트롤과 커서 표시
    showControls.value = true
    showCursor.value = true
    document.body.style.cursor = 'default'
  })

  // 재생 중 실시간으로 currentTime 업데이트
  player.on('timeupdate', ({ seconds }) => {
    currentTime.value = seconds

    // 구간반복이 설정되어 있고, 종료시점을 넘어갔을 때
    if (
      repeatStartTime.value !== null &&
      repeatEndTime.value !== null &&
      seconds > repeatEndTime.value
    ) {
      player.setCurrentTime(repeatStartTime.value)
    }
  })

  // 초기 duration 설정
  player.getDuration().then((totalDuration) => {
    duration.value = totalDuration
  })

  player.getChapters().then((chaptersData) => {
    chapters.value = chaptersData
  })

  // 전체화면 변경 이벤트 감지
  const handleFullscreenChange = () => {
    isFullscreen.value = !!document.fullscreenElement
  }

  document.addEventListener('fullscreenchange', handleFullscreenChange)

  // 초기 마운트 후 3초 뒤에 컨트롤과 커서 숨기기
  setTimeout(() => {
    if (!isPaused.value) {
      showControls.value = false
      showCursor.value = false
    }
  }, 3000)
})

onUnmounted(() => {
  resetTimers()
  // 모바일이 아닐 때만 커서 스타일 변경
  if (!isMobile.value) {
    document.body.style.cursor = 'default'
  }
  clearMediaStream()

  // 전체화면 이벤트 리스너 제거
  const handleFullscreenChange = () => {
    isFullscreen.value = !!document.fullscreenElement
  }

  document.removeEventListener('fullscreenchange', handleFullscreenChange)
})

const toggleCamera = async () => {
  // 웹캠을 끄는 경우 리소스 정리
  if (onWebCam.value) {
    clearMediaStream()
    onWebCam.value = false
    return
  }

  onWebCam.value = true

  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const videoDevices = devices.filter((device) => device.kind === 'videoinput')

    if (videoDevices.length === 0) {
      throw new Error('No video input devices found')
    }

    const constraints = {
      video: {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
    }
    mediaStream.value = await navigator.mediaDevices.getUserMedia(constraints)

    if (videoElement.value) {
      videoElement.value.srcObject = mediaStream.value
      videoElement.value.play()
    } else {
      console.error('Video element not found')
    }
  } catch (err) {
    if (err instanceof Error) {
      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        console.error('요청된 장치를 찾을 수 없습니다.')
      } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        console.error('카메라/마이크 접근 권한이 거부되었습니다.')
      } else {
        console.error('getUserMedia 오류:', err)
      }
    }
    onWebCam.value = false
  }
}

// 미디어 스트림 정리 함수
const clearMediaStream = () => {
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach((track) => {
      track.stop()
    })
    mediaStream.value = null
  }

  if (videoElement.value) {
    videoElement.value.srcObject = null
  }
}

const toggleChapters = () => {
  showChapters.value = !showChapters.value
}

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
}

const seekToChapter = async (startTime: number) => {
  const player = new Player('vimeo-player')
  const isPaused = await player.getPaused()

  // 현재 일시정지 상태를 저장
  const wasPaused = isPaused

  // 위치 이동
  await player.setCurrentTime(startTime)

  // 이전에 일시정지 상태였다면 다시 일시정지
  if (wasPaused) {
    await player.pause()
  }
}

const toggleFullscreen = async () => {
  if (!playerContainer.value) return

  try {
    if (!isFullscreen.value) {
      // 모바일 환경에서만 커스텀 전체화면 적용
      if (isMobile.value) {
        playerContainer.value.classList.add('custom-fullscreen')
        isFullscreen.value = true
        showControls.value = true
        showCursor.value = true
      } else {
        // PC 환경에서는 기본 requestFullscreen 사용
        await playerContainer.value.requestFullscreen()
      }
    } else {
      if (isMobile.value) {
        playerContainer.value.classList.remove('custom-fullscreen')
        isFullscreen.value = false
      } else {
        await document.exitFullscreen()
      }
    }
  } catch (err) {
    console.error('전체화면 전환 오류:', err)
  }
}

const handleFilterSelect = (option: FilterOption) => {
  selectedFilter.value = option
  currentSpeed.value = option.data as number
  const player = new Player('vimeo-player')
  player.setPlaybackRate(currentSpeed.value)
}

const togglePlay = async () => {
  const player = new Player('vimeo-player')
  const isPaused = await player.getPaused()
  if (isPaused) {
    player.play()
  } else {
    player.pause()
    // 일시정지 시 컨트롤러 표시
    showControls.value = true
    showCursor.value = true
  }
}

const handlePlayBarClick = async (event: MouseEvent) => {
  if (!duration.value) return

  const playBar = event.currentTarget as HTMLElement
  const rect = playBar.getBoundingClientRect()
  const clickPosition = event.clientX - rect.left
  const percentage = clickPosition / rect.width
  const seekTime = duration.value * percentage

  const player = new Player('vimeo-player')
  const isPaused = await player.getPaused()

  // 현재 일시정지 상태를 저장
  const wasPaused = isPaused

  // 위치 이동
  await player.setCurrentTime(seekTime)

  // 잠시 재생 후 멈추기
  if (wasPaused) {
    await player.play()
    await player.pause()
  }
}

const toggleRepeat = async () => {
  if (
    repeatMode.value === 'none' &&
    repeatStartTime.value === null &&
    repeatEndTime.value === null
  ) {
    // 구간반복 시작시점 설정 모드로 전환
    repeatMode.value = 'start'
  } else if (repeatMode.value === 'start') {
    // 구간반복 시작시점 저장
    repeatStartTime.value = currentTime.value
    repeatMode.value = 'end'
  } else if (repeatMode.value === 'end') {
    // 구간반복 종료시점 저장
    repeatEndTime.value = currentTime.value
    repeatMode.value = 'none'
  } else {
    // 구간반복 종료 상태에서 버튼을 누르면 모든 설정 초기화
    repeatStartTime.value = null
    repeatEndTime.value = null
    repeatMode.value = 'none'
  }
}

const getRepeatText = () => {
  if (repeatMode.value === 'start') {
    return '구간반복 시작시점에서 클릭'
  } else if (repeatMode.value === 'end') {
    return '구간반복 종료시점에서 클릭'
  } else if (repeatStartTime.value !== null && repeatEndTime.value !== null) {
    return '구간반복 종료'
  }
  return ''
}

const getRepeatIcon = () => {
  if (repeatMode.value === 'start' || repeatMode.value === 'end') {
    return RepeatRedIcon
  } else if (repeatStartTime.value !== null && repeatEndTime.value !== null) {
    return RepeatWhiteIcon
  }
  return RepeatWhiteIcon
}

const getRepeatContainerClass = () => {
  if (repeatMode.value === 'start' || repeatMode.value === 'end') {
    return 'repeat-container setting'
  } else if (repeatStartTime.value !== null && repeatEndTime.value !== null) {
    return 'repeat-container active'
  }
  return 'repeat-container'
}

const resetTimers = () => {
  if (controlsTimeout.value) {
    clearTimeout(controlsTimeout.value)
  }
  if (cursorTimeout.value) {
    clearTimeout(cursorTimeout.value)
  }
}

const handleMouseEnter = () => {
  showControls.value = true
  showCursor.value = true
  document.body.style.cursor = 'default'
  resetTimers()

  if (!isPaused.value) {
    cursorTimeout.value = window.setTimeout(() => {
      showCursor.value = false
      document.body.style.cursor = 'none'
    }, 3000)

    controlsTimeout.value = window.setTimeout(() => {
      showControls.value = false
    }, 3000)
  }
}

const handleMouseMove = () => {
  showControls.value = true
  showCursor.value = true
  // 모바일이 아닐 때만 커서 스타일 변경
  if (!isMobile.value) {
    document.body.style.cursor = 'default'
  }
  resetTimers()

  if (!isPaused.value) {
    cursorTimeout.value = window.setTimeout(() => {
      showCursor.value = false
      // 모바일이 아닐 때만 커서 스타일 변경
      if (!isMobile.value) {
        document.body.style.cursor = 'none'
      }
    }, 3000)

    controlsTimeout.value = window.setTimeout(() => {
      showControls.value = false
    }, 3000)
  }
}

const handleMouseLeave = () => {
  if (!isPaused.value) {
    showControls.value = false
  }
  showCursor.value = true
  // 모바일이 아닐 때만 커서 스타일 변경
  if (!isMobile.value) {
    document.body.style.cursor = 'default'
  }
  resetTimers()
}

const handleTouchStart = () => {
  showControls.value = true
  showCursor.value = true
  resetTimers()

  if (!isPaused.value) {
    cursorTimeout.value = window.setTimeout(() => {
      showCursor.value = false
    }, 3000)

    controlsTimeout.value = window.setTimeout(() => {
      showControls.value = false
    }, 3000)
  }
}

const handleTouchMove = () => {
  showControls.value = true
  showCursor.value = true
  resetTimers()

  if (!isPaused.value) {
    cursorTimeout.value = window.setTimeout(() => {
      showCursor.value = false
    }, 3000)

    controlsTimeout.value = window.setTimeout(() => {
      showControls.value = false
    }, 3000)
  }
}

const handleTouchEnd = () => {
  if (!isPaused.value) {
    showControls.value = false
  }
  showCursor.value = true
  resetTimers()
}
</script>

<template>
  <div
    class="video-player"
    ref="playerContainer"
    :class="{ 'hide-cursor': !shouldShowCursor, 'is-ios': isIOS }"
    @mouseenter="handleMouseEnter"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <div class="video-menus" :class="{ show: shouldShowControls }">
      <BaseFilter
        :options="filterOptions"
        :selectedOption="selectedFilter"
        customTrigger
        @select="handleFilterSelect"
        menuAlign="end"
      >
        <template #trigger>
          <span class="cursor-pointer body-lg white" :src="AdjustSpeedIcon" alt="adjust_speed"
            >{{ currentSpeed }}x</span
          >
        </template>
      </BaseFilter>
      <div :class="getRepeatContainerClass()" @click="toggleRepeat">
        <img class="cursor-pointer" :src="getRepeatIcon()" alt="repeat" />
        <span v-if="getRepeatText()" class="repeat-text">{{ getRepeatText() }}</span>
      </div>
      <img
        class="cursor-pointer"
        :class="onWebCam ? '' : 'svg-white'"
        :src="onWebCam ? OffCameraIcon : OpenCameraIcon"
        @click="toggleCamera"
      />
      <img
        v-if="chapters.length > 0"
        class="cursor-pointer svg-white"
        :src="MenuIcon"
        @click="toggleChapters"
      />
    </div>
    <div class="video-playback-controls" :class="{ show: shouldShowControls }">
      <div class="video-playback-controls-left">
        <img
          class="svg-white svg-24 cursor-pointer"
          :src="isPaused ? PlayIcon : PauseIcon"
          alt="play"
          @click="togglePlay"
        />
      </div>
      <div class="video-playback-controls-center">
        <div class="video-playback-controls-play-bar" @click="handlePlayBarClick">
          <div
            class="video-playback-controls-play-bar-progress"
            :style="{ width: `${(currentTime / duration) * 100}%` }"
          ></div>
        </div>
      </div>
      <div class="video-playback-controls-right cursor-pointer" @click="toggleFullscreen">
        <img
          class="svg-white svg-24"
          :src="isFullscreen ? FullScreenOffIcon : FullScreenIcon"
          alt="fullscreen"
        />
      </div>
    </div>
    <div
      class="video-player-container"
      :class="{
        'with-webcam': onWebCam,
        'with-chapters': showChapters,
        'with-webcam-and-chapters': onWebCam && showChapters,
      }"
    >
      <div
        class="video-player-vimeo"
        :class="{
          'with-webcam': onWebCam,
          'with-chapters': showChapters,
          'with-webcam-and-chapters': onWebCam && showChapters,
        }"
      >
        <iframe id="vimeo-player" :src="vimeoUrl" frameborder="0" allow="autoplay"></iframe>
        <div
          class="mouse-event-layer"
          :class="{ 'hide-cursor': !shouldShowCursor }"
          @mouseenter="handleMouseEnter"
          @mousemove="handleMouseMove"
          @mouseleave="handleMouseLeave"
        ></div>
      </div>
      <div v-show="onWebCam" class="video-player-webcam">
        <video class="video-player-webcam-video" ref="videoElement" playsinline></video>
      </div>
      <div v-show="showChapters" class="video-player-chapters">
        <div class="chapters-header">
          <img
            class="cursor-pointer svg-white svg-24"
            :src="CloseIcon"
            alt="close"
            @click="toggleChapters"
          />
        </div>
        <PerfectScrollbar>
          <div class="chapters-content">
            <ul v-if="chapters.length > 0" class="chapters-list">
              <li
                v-for="chapter in chapters"
                :key="chapter.startTime"
                @click="seekToChapter(chapter.startTime)"
                class="chapter-item"
              >
                <div class="body-lg white">{{ chapter.title }}</div>
                <div class="body-sm white">{{ formatTime(chapter.startTime) }}</div>
              </li>
            </ul>

            <div v-else class="no-chapters">챕터 정보가 없습니다.</div>
          </div>
        </PerfectScrollbar>
      </div>
      <div class="center-play-button" :class="{ show: shouldShowControls }" @click="togglePlay">
        <div class="outer-circle">
          <div class="inner-circle">
            <img :src="isPaused ? PlayRedIcon : PauseIcon" alt="play" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.video-player {
  width: 100%;
  height: 100%;
  background-color: #000;
  position: relative;
  cursor: default;
}

.video-player.hide-cursor {
  cursor: none;
}

.video-menus {
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  width: 100%;
  gap: 20px;
  padding: 20px 25px 16px 0px;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  background: linear-gradient(180deg, rgba(33, 33, 33, 0.3) 0%, rgba(33, 33, 33, 0) 100%);
}

.video-playback-controls {
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  width: calc(100% - 40px);
  gap: 16px;
  position: absolute;
  bottom: 20px;
  z-index: 10;
  left: 20px;
  background-color: var(--gray-900);
  border-radius: 8px;
  padding: 6px 20px;
  margin: 0;
}

.video-playback-controls img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.video-playback-controls-left,
.video-playback-controls-right {
  display: flex;
  align-items: center;
  line-height: 0;
}

.video-playback-controls-center {
  flex: 1;
  display: flex;
  align-items: center;
}

.video-playback-controls-play-bar {
  width: 100%;
  height: 8px;
  background-color: var(--gray-800);
  border-radius: 4px;
  position: relative;
  cursor: pointer;
}

.video-playback-controls-play-bar-progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: var(--tint-500);
  border-radius: 4px;
  width: 0%; /* 이 부분은 JavaScript로 동적으로 조절되어야 합니다 */
}

.fullscreen-icon,
.chapters-icon {
  width: 24px;
  height: 24px;
  fill: white;
}

.video-player-container {
  width: 1280px;
  height: 720px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  position: relative;
}

.video-player-container.hide-cursor {
  cursor: none;
}

.video-player-vimeo {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
}

.video-player-vimeo.with-webcam {
  position: absolute;
  left: 0;
  width: 640px;
  height: 720px;
  z-index: 1;
}

.video-player-vimeo.with-chapters {
  width: calc(100%);
  left: 0;
}

.video-player-vimeo.with-webcam-and-chapters {
  width: calc(100%);
}

#vimeo-player {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.video-player-vimeo.with-webcam #vimeo-player {
  width: 1280px;
  height: 720px;
  position: absolute;
  left: -320px; /* 중앙 영역이 보이도록 조정 */
}

.video-player-webcam {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  right: 0;
  width: calc(1280px - 640px);
  height: 720px;
  z-index: 1;
}

.video-player-webcam-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transform: scaleX(-1);
}

.video-player-chapters {
  position: absolute;
  right: 0;
  width: 300px;
  height: 100%;
  background-color: var(--gray-800);
  padding: 20px;
  z-index: 9999999;
  display: flex;
  flex-direction: column;
}

.chapters-header {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 20px;
}

.chapters-header img {
  width: 24px;
  height: 24px;
}

.chapters-content {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
}

.chapters-content::-webkit-scrollbar {
  width: 4px;
}

.chapters-content::-webkit-scrollbar-track {
  background: transparent;
}

.chapters-content::-webkit-scrollbar-thumb {
  background: var(--gray-600);
  border-radius: 2px;
}

.chapters-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.chapter-item {
  cursor: pointer;
  margin-bottom: 20px;
}

.chapter-item:last-child {
  margin-bottom: 0;
}

.chapter-title {
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 4px;
}

.chapter-time {
  color: var(--gray-400);
  font-size: 14px;
}

.no-chapters {
  color: var(--gray-400);
  font-size: 14px;
  text-align: center;
}

/* 전체화면 스타일 */
:fullscreen .video-player-container {
  width: 100%;
  height: 100vh;
  display: flex;
}

:fullscreen .video-player-vimeo.with-webcam {
  position: absolute;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

:fullscreen .video-player-vimeo.with-chapters {
  width: calc(100%);
}

:fullscreen .video-player-vimeo.with-webcam-and-chapters {
  width: calc(100%);
}

:fullscreen .video-player-vimeo.with-webcam #vimeo-player {
  width: 100%;
  height: 100%;
  position: absolute;
  left: -25%; /* 중앙 영역이 보이도록 조정 */
}

:fullscreen .video-player-webcam {
  position: absolute;
  right: 0;
  width: 50%;
  height: 100%;
}

:fullscreen .video-player-webcam-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

:fullscreen .video-player-chapters {
  height: 100vh;
}

/* 브라우저 호환성을 위한 추가 스타일 */
:-webkit-full-screen .video-player-container {
  width: 100vw;
  height: 100vh;
  max-width: none;
  display: flex;
}

:-webkit-full-screen .video-player-vimeo.with-webcam {
  position: absolute;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

:-webkit-full-screen .video-player-vimeo.with-chapters {
  width: calc(100%);
}

:-webkit-full-screen .video-player-vimeo.with-webcam-and-chapters {
  width: calc(100%);
}

:-webkit-full-screen .video-player-vimeo.with-webcam #vimeo-player {
  width: 100%;
  height: 100%;
  position: absolute;
  left: -25%; /* 중앙 영역이 보이도록 조정 */
}

:-webkit-full-screen .video-player-webcam {
  position: absolute;
  right: 0;
  width: 50%;
  height: 100%;
}

:-webkit-full-screen .video-player-webcam-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

:-webkit-full-screen .video-player-chapters {
  height: 100vh;
}

:-ms-fullscreen .video-player-container {
  width: 100%;
  height: 100vh;
  display: flex;
}

:-ms-fullscreen .video-player-vimeo.with-webcam {
  position: absolute;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

:-ms-fullscreen .video-player-vimeo.with-chapters {
  width: calc(100%);
}

:-ms-fullscreen .video-player-vimeo.with-webcam-and-chapters {
  width: calc(100%);
}

:-ms-fullscreen .video-player-vimeo.with-webcam #vimeo-player {
  width: 100%;
  height: 100%;
  position: absolute;
  left: -25%; /* 중앙 영역이 보이도록 조정 */
}

:-ms-fullscreen .video-player-webcam {
  position: absolute;
  right: 0;
  width: 50%;
  height: 100%;
}

:-ms-fullscreen .video-player-webcam-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

:-ms-fullscreen .video-player-chapters {
  height: 100vh;
}

@media (max-width: 1365px) {
  .video-player-container {
    width: 100%;
    height: auto;
    max-width: 600px;
    display: flex;
    flex-direction: column;
    justify-content: start;
    position: relative;
    margin: 0 auto; /* 중앙 정렬 */
  }

  .video-player-vimeo {
    width: 100%;
    height: auto;
    max-width: 600px;
    aspect-ratio: 16 / 9;
    position: static;
  }

  .video-menus {
    padding: 12px 20px 12px 0;
  }

  .video-player-vimeo.with-webcam {
    width: 100%;
    height: auto;
    max-width: 600px;
    aspect-ratio: 16 / 9;
    position: static;
  }

  .video-player-vimeo.with-webcam #vimeo-player {
    width: 100%;
    height: auto;
    max-width: 600px;
    aspect-ratio: 16 / 9;
    position: static;
  }

  .video-player-webcam {
    flex: none;
    position: static;
    justify-content: center;
    align-items: center;
    right: 0;
    width: 100%;
    height: auto;
    max-width: 600px;
    aspect-ratio: 16 / 9;
    z-index: 1;
    overflow: hidden;
    margin: 0 auto; /* 중앙 정렬 */
  }

  .video-player-webcam-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    aspect-ratio: 16 / 9;
    transform: scaleX(-1);
  }

  /* 모든 기기에서 좌우반전 적용 */
  .video-player .video-player-webcam-video {
    transform: scaleX(-1);
  }

  /* 전체화면일 때도 좌우반전 적용 */
  :fullscreen .video-player .video-player-webcam-video,
  :-webkit-full-screen .video-player .video-player-webcam-video,
  :-ms-fullscreen .video-player .video-player-webcam-video {
    transform: scaleX(-1);
  }

  /* 커스텀 전체화면일 때의 스타일 */
  .video-player.custom-fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 999999;
    background-color: #000;
    pointer-events: auto;
  }

  .video-player.custom-fullscreen::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: #000;
    z-index: -1;
    pointer-events: none;
  }

  .video-player.custom-fullscreen ~ * {
    pointer-events: none;
  }

  .video-player.custom-fullscreen .video-player-container {
    width: 100vw;
    height: 100vh;
    max-width: none;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    background-color: #000;
    overflow: hidden;
    position: relative;
  }

  .video-player.custom-fullscreen .video-menus {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 1000000;
  }

  .video-player.custom-fullscreen .video-playback-controls {
    position: fixed;
    bottom: 20px;
    left: 20px;
    z-index: 1000000;
  }

  .video-playback-controls {
    width: calc(100% - 40px);
    bottom: 10px;
    left: 20px;
    padding: 8px 16px;
  }

  .video-playback-controls-center {
    width: 100%;
  }

  .video-playback-controls-play-bar {
    width: 100%;
  }

  .video-player.custom-fullscreen .video-player-vimeo {
    width: 50%;
    height: 100%;
    max-width: none;
    position: relative;
    overflow: hidden;
    z-index: 1;
  }

  .video-player.custom-fullscreen .video-player-vimeo.with-webcam {
    width: 50%;
    height: 100%;
    max-width: none;
    position: relative;
    overflow: hidden;
    z-index: 1;
  }

  .video-player.custom-fullscreen .video-player-vimeo.with-webcam #vimeo-player {
    width: 200%;
    height: 100%;
    max-width: none;
    position: absolute;
    left: -50%;
    object-fit: cover;
  }

  .video-player.custom-fullscreen .video-player-webcam {
    width: 50%;
    height: 100%;
    max-width: none;
    position: relative;
    overflow: hidden;
    z-index: 2;
  }

  .video-player.custom-fullscreen .video-player-webcam-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transform: scaleX(-1);
  }

  .video-player.hide-cursor,
  .mouse-event-layer.hide-cursor {
    cursor: default !important;
  }
}

/* PC 버전에서는 커스텀 전체화면 스타일 제거 */
@media (min-width: 1366px) {
  .video-player.custom-fullscreen {
    position: static;
    width: 100%;
    height: 100%;
    z-index: auto;
    background-color: transparent;
  }

  .video-player.custom-fullscreen::before {
    display: none;
  }

  .video-player.custom-fullscreen ~ * {
    pointer-events: auto;
  }

  .video-player-webcam-video {
    transform: scaleX(-1);
  }
}

/* iOS 기기에서만 좌우반전 적용 */
.video-player.custom-fullscreen.is-ios .video-player-webcam-video {
  transform: scaleX(-1);
}

.repeat-container {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 8px;
  cursor: pointer;
}

.repeat-container.setting {
  background-color: var(--tint-100);
}

.repeat-container.active {
  background-color: var(--tint-500);
}

.repeat-text {
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 18px */
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}

.repeat-container.setting .repeat-text {
  color: var(--tint-500);
  text-decoration: underline;
}

.repeat-container.active .repeat-text {
  color: white;
}

.center-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 10;
}

.outer-circle {
  width: 80px;
  height: 80px;
  background-color: rgba(33, 33, 33, 0.2);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease;
}

.inner-circle {
  width: 60px;
  height: 60px;
  background-color: var(--white);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease;
}

.inner-circle img {
  width: 24px;
  height: 24px;
}

.center-play-button:hover .outer-circle {
  width: 100px;
  height: 100px;
}

.center-play-button:hover .inner-circle {
  background-color: var(--tint-500);
}

.center-play-button:hover .inner-circle img {
  filter: brightness(0) invert(1); /* 흰색으로 변경 */
}

.video-menus,
.video-playback-controls,
.center-play-button {
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
}

.video-menus.show,
.video-playback-controls.show,
.center-play-button.show {
  opacity: 1;
  visibility: visible;
}

.mouse-event-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 5;
}

.mouse-event-layer.hide-cursor {
  cursor: none;
}

/* 전체화면 모드에서도 동일하게 적용 */
:fullscreen .mouse-event-layer.hide-cursor {
  cursor: none;
}

:-webkit-full-screen .mouse-event-layer.hide-cursor {
  cursor: none;
}

:-ms-fullscreen .mouse-event-layer.hide-cursor {
  cursor: none;
}
</style>
